import React, { useState, useEffect, useRef } from 'react';
import { useReactToPrint } from "react-to-print";
import './App.css';
import logo from './logo.svg';

function App() {
    const [hid, setHID] = useState('');
    const [data, setData] = useState(null);
    const [userName, setUserName] = useState('');
    const [userCompany, setUserCompany] = useState('');
    const [userTitle, setUserTitle] = useState('');
    const [userEmail, setUserEmail] = useState('');
    const [date, setDisplayDate] = useState('');
    const [dateTime, setDateTime] = useState('');
    const [loading, setLoading] = useState(true);
    const [userNameError, setUserNameError] = useState('');
    const [userCompanyError, setUserCompanyError] = useState('');
    const [userTitleError, setUserTitleError] = useState('');
    const [userEmailError, setUserEmailError] = useState('');
    // error message if query to NRL list fails or pulls inactive row
    const [errorMessage, setErrorMessage] = useState('');
    const [apiError, setApiError] = useState(false);
    const [inactiveRowMessage, setInactiveRowMessage] = useState('');
    const [isInactive, setInactive] = useState(false);
    // success message and when to show that message along with disabling submit button
    const [message, setMessage] = useState('');
    const [showMessage, setShowMessage] = useState(false);
    const [haveSubmitted, setHaveSubmitted] = useState(false);
    // Refs for the content to print
    const contentRef = useRef();
    const reactToPrintFn = useReactToPrint({ contentRef });


    useEffect(() => {
        // Set the current date when the component mounts
        let currentDate = new Date().toISOString().split('T')[0];
        let currentDateTime = new Date().toISOString();
        let currentTimezoneOffset = new Date().getTimezoneOffset();
        let offsetValue = currentTimezoneOffset / 60;
        let offsetValueString = offsetValue.toString();
        let currentDateTimeString = currentDateTime.toString();
        let adjustedDateTimeString = currentDateTimeString + offsetValueString;
        setDateTime(adjustedDateTimeString);
        setDisplayDate(currentDate)

        const params = new URLSearchParams(window.location.search);
        const hidParam = params.get('id');

        if (hidParam) {
            setHID(hidParam);
        }

        const fetchData = async () => {
            try {
                const response = await fetch(`https://nrlfunctions.azurewebsites.net/api/getNRLData?id=${encodeURIComponent(hidParam || hid)}`, {
                    method: 'GET'
                });

                if (!response.ok) {
                    throw new Error('Failed to fetch data from the server.');
                }

                const responseData = await response.json();
                if (!responseData || Object.keys(responseData).length === 0 || responseData.errorMessage === "invalid HashID") {
                    setApiError(true);
                    setErrorMessage('No data was found. Please check your URL or reach out to your West Monroe contacts for help.');
                } else if (responseData.isActive === false) {
                    setInactive(true);
                    setInactiveRowMessage('This unique link is no longer active. Please reach out to your West Monroe contacts for help.');
                } else {
                    setData(responseData);
                }

                setLoading(false);
            } catch (error) {
                console.error('Error:', error);
                setApiError(true);
                setErrorMessage('An error occurred while loading the page. Please try again later.');
                setLoading(false);
            }
        };

        fetchData();
    }, [hid]);

    const handleSubmit = async (event) => {
        event.preventDefault();

        try {
            const response = await fetch('https://nrlfunctions.azurewebsites.net/api/postNRLResponse', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    userName: userName.trim(),
                    userEmail: userEmail.trim(),
                    userCompany: userCompany.trim(),
                    userTitle: userTitle.trim(),
                    date: dateTime,
                    hashID: hid
                }),
            });

            if (!response.ok) {
                throw new Error('Network response was not ok');
            }

            const result = await response.json();
            console.log('Success:', result);
            // Handle success (e.g., show a success message or redirect)
            if (result.ResponseMessage) {

                setMessage("Your acceptance has successfully been recorded. You will receive a confirmation email shortly from West Monroe.");
                setShowMessage(true);
                setHaveSubmitted(true);
            }
        } catch (error) {
            console.error('Error:', error);
            // Handle error (e.g., show an error message)
        }
    };

    useEffect(() => {
        let messageTimer;
        if (showMessage) {
            messageTimer = setTimeout(() => {
                setShowMessage(false);
            }, 8000); // Show message for 8 seconds
        }
        return () => clearTimeout(messageTimer);
    }, [showMessage]);

    // Function to check if a string contains only ASCII characters
    const isAscii = (str) => /^[\u0020-\u007E]*$/.test(str);

    // Function to validate email format
    const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

    // Determine if the submit button should be disabled
    const isSubmitDisabled = () => {
        return !userName || !userEmail || !userCompany || !userTitle || !date || userName.length > 200 || userEmail.length > 200 || !isAscii(userName) || !isAscii(userEmail) || !isValidEmail(userEmail) || !isAscii(userTitle) || !isAscii(userCompany) || haveSubmitted;
    };
    // Function to see if a string is empty or blank
    const isEmptyOrBlank = (str) => {
        return !str || str.trim().length === 0;
    };

    // Function to format regionText with line breaks
    const formatRegionText = (text) => {
         // Check if the text is null, undefined, or empty
    if (!text || typeof text !== 'string') {
        return null; }
        text.split('\n\n').map((part, index, parts) => (
            <React.Fragment key={index}>
                {part}
                {index < parts.length - 1 && <><br /><br /></>}
            </React.Fragment>
        ))};

    // FAQ Modal
    useEffect(() => {
        const showFAQ = document.getElementById("showFAQ");
        const faqDialog = document.getElementById("faqDialog");
        showFAQ.addEventListener("click", () => {
            faqDialog.showModal();
        })
    }, []);

    return (
        <div className="App">
            <div className="App-nav">
                <a href="/">
                    <img src={logo} alt="Logo" />
                </a>
                <ul>
                    <li>
                        <button id="showFAQ">NRL FAQ</button>
                    </li>
                    <li>
                        <a href="https://westmonroe.com">West Monroe Home</a>
                    </li>
                </ul>
            </div>
            <dialog className="faq" id="faqDialog">
                <form method="dialog">
                    <h3 className="faqHeader">RECEIVING A WEST MONROE REPORT - GUIDE TO WEST MONROE’S ONLINE NRL ACCEPTANCE PROCESS</h3>
                    <p>West Monroe has implemented a new solution for accelerating access by third parties to M&A diligence reports: the Online Non-Reliance Letter. This solution provides standardized, “click-to-accept” terms that govern the release and use of reports authored by West Monroe for its clients.</p>

                    <h4>Q1: Why do I need to accept the terms of the Online Non-Reliance Letter?</h4>
                    <p>A1: The purpose of this letter is to clarify that the report was created by West Monroe for its client’s internal business use and not intended for use or reliance by third parties. To the extent West Monroe clients need to share these reports with third parties, West Monroe requires such third parties to first agree to keep the information confidential and not to rely on the contents of the report for their own use. To receive the report, you must review and click to accept the online access terms (the “Terms”) of the Online Non-Reliance Letter. This confirms that you understand and agree to the Terms outlined, and that you have the authority to act on behalf of the organization you represent.</p>

                    <h4>Q2: How should I fill in the Name, Company, Title, and Email fields?</h4>
                    <p>A2: Please fill in these fields as you normally would when signing documents for your company. Use the legal name for the "Company" field, the signer’s full name for the "Name" field, the signer’s job title for the "Title" field, and the signer’s work email address for the "Email" field. This helps us keep accurate records, and we won't share your information beyond what's needed to provide you with the report.</p>

                    <h4>Q3: How will I know my acceptance was successful?</h4>
                    <p>A3: After clicking the “Accept” button, you will see a notification display in the lower-right corner of the page that provides confirmation. You will also have the option to use the “Print/Save” button to create a PDF copy of the accepted terms for your records.</p>

                    <h4>Q4: Does the sender receive a notification when I accept?</h4>
                    <p>A4: Yes, the person who sent you the report will automatically receive a notification letting them know that you’ve accepted the Terms.</p>

                    <h4>Q5: What happens after I accept?</h4>
                    <p>A5: You will receive an email that you should then forward to your lender who will, in turn, provide you with a copy of the report.</p>

                    <h4>Q6: What should we do if we already have existing precedent NRL terms (“Precedent”) with West Monroe and do not want to use this process?</h4>
                    <p>A6: If you have Precedent with West Monroe, please reach out to your West Monroe contact to obtain a copy of your organization’s Precedent template to sign manually. [Whether you access the WM report under the Terms or Precedent, you should still click “accept” in order for your acceptance to be recorded on our system; without that we cannot process your request to see the WM Report].</p>

                    <h4>Q7: Can I choose not to use this Online Non-Reliance Letter and e-sign a copy of the letter instead?</h4>
                    <p>A7: Yes, if you would prefer not to use the “click to accept” terms, please reach out to your West Monroe contact to obtain a PDF of the non-reliance letter for review and signature.</p>

                    <h4>Q8: Who may we share the report with?</h4>
                    <p>A8: According to the Terms, you may share it within your organization and its affiliates (including employees, directors and officers) and with your external legal counsel because they are subject to professional obligations of secrecy. You may not share it with any other party, including (but not limited to), the entities listed below:</p>
                    <ul>
                        <li>Potential co-investors and/or consortium members</li>
                        <li>Potential sources of finance</li>
                        <li>Re-insurers</li>
                        <li>Advisors (financial advisors, professional advisors, etc.)</li>
                        <li>Consultants</li>
                        <li>Other third parties with whom you have signed NDAs or back-to-backs</li>
                    </ul>
                    <p>Of course, all these parties may receive the West Monroe report if West Monroe and its client consent, but such parties will be separately required to accept the Terms before they may receive the report. If you would like any such party to be sent an access link, please contact the person who is arranging for you to receive the West Monroe report.</p>
                    <div>
                        <button className="wm-button">Close FAQ</button>
                    </div>
                </form>
            </dialog>
            <div>
                {apiError ? (<div className='error-alert'>
                        <h2>Error: {errorMessage}</h2>
                        </div>
                ) : isInactive ? (<div className='inactive-alert'>
                        <h2>{inactiveRowMessage}</h2>
                        </div>
                ) : loading ? (
                        <h1>Please Wait...Loading Data</h1>
                ) : (
                    <div className="App-body">
                        {data && (
                            <div className="content-wrapper">
                                <div className="content" ref={contentRef}>
                                    <div className="print-only-visible">
                                        <img src={logo} alt="Logo" />
                                        <p>
                                            <br />
                                            <br />
                                            {userName}<br />
                                            {userCompany}<br />
                                            {date}<br />
                                        </p>
                                        <p>Dear {userName},</p>
                                    </div>
                                    <p>{data.regionCompany} ("WM") was engaged by <strong>{data.client}</strong> (“the Company”) to perform certain business consulting services pursuant to an agreement between WM and the Company. In connection with that engagement, WM was asked to prepare a report, <strong>{data.reportDescription}</strong> (such report, whether now or subsequently provided to you, including any information related to the report and any supplements or updates to the report, whether in written or oral form, that may be now or subsequently provided to you by or on behalf of WM, the "Deliverable") for the Company’s internal business purposes.</p>
                                    <p>It is our understanding that <strong>{userCompany}</strong> (the "Recipient") has requested a copy of the Deliverable from the Company for your own use. The Company has requested that WM allow disclosure of the Deliverable to the Recipient. Although WM’s professional relationship for this particular engagement is with the Company and does not formally extend to the Recipient, WM will consent to the disclosure of the Deliverable solely for informational purposes in connection with your review of the target company listed in <strong>{data.project}</strong> ("Target"). However, prior to consenting to such disclosure of the Deliverables to the Recipient, we are required to obtain the written acceptances specified in this letter from you.</p>
                                    <p>Please acknowledge the following by filling in the details below and clicking 'ACCEPT'.</p>
                                    <p>WM's services were and are performed on behalf and solely for the use of the Company, in reliance upon representations, warranties and other information provided to us by the Company in the performance of those services and the preparation of the Deliverables. This engagement of WM did not and does not contemplate the use of WM’s work by others, including the Recipient. Therefore, items of possible interest to the Recipient may not have been and may not be specifically addressed. WM’s use of professional judgment and the assessment of materiality for the purposes of their work for us means that matters may have existed or may exist that would be assessed differently by the Recipient. Neither WM nor the Company makes any representations as to the sufficiency or appropriateness of the Deliverables for the Recipient’s purposes. For the foregoing reasons, WM’s work should not be taken to supplant other inquiries and procedures that the Recipient should undertake for the purpose of satisfying itself regarding any matter relating to an investment in Target. Neither WM nor the Company has any obligation to the Recipient to update or supplement the Deliverables or further investigate or update any matter that may or may not be included therein, or to inform the Recipient if any of the information or statements contained in the Deliverables should subsequently be modified or determined to be incorrect.</p>
                                    <p className="page-break"></p>
                                    <div className="print-only-visible"><br /><img src={logo} alt="Logo" /><br /><br /></div>
                                    <p>In consideration of the Company providing the Recipient access to WM's Deliverables, the Recipient agrees that as a result of its access to the Deliverables or by virtue of this letter, (a) WM does not assume any duties or obligations to the Recipient; and (b) the Recipient shall have no right of action of any type or kind against WM, its affiliates or any of their personnel arising from or relating to WM's services and the Deliverables, and the Recipient hereby expressly releases WM, its affiliates and their personnel from liability for any such claim. This letter and the Deliverables are provided solely for the internal use of the Recipient and may not be provided or disclosed, in whole or in part, to any other party, provided, however, the Recipient is permitted to disclose the Deliverables (a) to the Recipient’s affiliates and to the Recipient’s and its affiliates’ respective officers, directors, employees and legal advisors (collectively, its “Representatives”) to the extent such parties have a need to know such information, provided the Recipient shall be responsible for any breach of this letter agreement by any Representative as if the Representative were the Recipient hereunder, and (b) to the extent required by valid and applicable law, regulation or legal process, provided that to the extent legally permitted, the Recipient will give WM prompt written notice prior to any such disclosure other than for routine audit requests not specifically targeted at WM, the Company or the Deliverables. The Recipient agrees to treat the Deliverables as strictly confidential, to use them solely for informational purposes in connection with the potential investment in Target, and to limit disclosure to only those Representatives who have been advised of and agreed to the obligations hereunder.</p>
                                    <p>{formatRegionText(data.regionText)}</p>
                                    <p>Please confirm your agreement with the foregoing by submitting the details below.</p>
                                    <div className="print-only-visible">
                                        <p>Sincerely,</p>
                                        <p>{data.regionCompany}</p>
                                        <p>Acknowledged and Agreed:</p>
                                        <div className="signature-container">
                                            <div className="signature">
                                                <u>{userName}, {userTitle}</u>
                                                <br />
                                                <i>Name, Title</i>
                                            </div>
                                            <br />
                                            <div className="signature">
                                                <u>{date}</u>
                                                <br />
                                                <i>Date</i>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                )}
                <form className="input-form" onSubmit={handleSubmit}>
                    <div className="form-group">
                        <label htmlFor="userName">Name</label>
                        <input type="text" id="userName" name="userName" maxLength="200" value={userName} onChange={(e) => {
                            const userNameValue = e.target.value;
                            setUserName(userNameValue);

                            if (isEmptyOrBlank(userNameValue)) {
                                setUserNameError('Required field');
                            } else if (!isAscii(userNameValue)) {
                                setUserNameError('Invalid characters present');
                            } else {
                                setUserNameError('');
                            }
                        }}
                        />
                        {userNameError && <div className="error-message">{userNameError}</div>}
                    </div>
                    <div className="form-group">
                        <label htmlFor="userCompany">Company</label>
                        <input type="text" id="userName" name="userCompany" maxLength="200" value={userCompany} onChange={(e) => {
                            const userCompanyValue = e.target.value;
                            setUserCompany(userCompanyValue);

                            if (isEmptyOrBlank(userCompanyValue)) {
                                setUserCompanyError('Required field');
                            } else if (!isAscii(userCompanyValue)) {
                                setUserCompanyError('Invalid characters present');
                            } else {
                                setUserCompanyError('');
                            }
                        }}
                        />
                        {userCompanyError && <div className="error-message">{userCompanyError}</div>}
                    </div>
                    <div className="form-group">
                        <label htmlFor="userTitle">Title</label>
                        <input type="text" id="userTitle" name="userTitle" maxLength="200" value={userTitle} onChange={(e) => {
                            const userTitleValue = e.target.value;
                            setUserTitle(userTitleValue);

                            if (isEmptyOrBlank(userTitleValue)) {
                                setUserTitleError('Required field');
                            } else if (!isAscii(userTitleValue)) {
                                setUserTitleError('Invalid characters present');
                            } else {
                                setUserTitleError('');
                            }
                        }}
                        />
                        {userTitleError && <div className="error-message">{userTitleError}</div>}
                    </div>
                    <div className="form-group">
                        <label htmlFor="userEmail">Email</label>
                        <input type="email" id="userEmail" name="userEmail" maxLength="200" value={userEmail} onChange={(e) => {
                            const userEmailValue = e.target.value;
                            setUserEmail(userEmailValue);

                            if (isEmptyOrBlank(userEmailValue)) {
                                setUserEmailError('Required field');
                            } else if (!isAscii(userEmailValue)) {
                                setUserEmailError('Invalid characters present');
                            } else if (!isValidEmail(userEmailValue)) {
                                setUserEmailError('Invalid email address');
                            } else {
                                setUserEmailError('');
                            }
                        }}
                        />
                        {userEmailError && <div className="error-message">{userEmailError}</div>}
                    </div>
                    <div className="form-group">
                        <label htmlFor="date">Date</label>
                        <input type="date" id="date" name="date" value={date} disabled />
                    </div>
                    <div>
                        <button className="wm-button" type="submit" disabled={isSubmitDisabled()}>accept</button>
                    </div>
                    <div>
                        <button className="wm-button" type="button" disabled={!haveSubmitted} onClick={() => {
                            reactToPrintFn()
                        }}>Print/Save</button>
                    </div>
                </form>
            </div>
                                    )}
                                    </div>
            {showMessage && (
                <div className="message-popup">
                    {message}
                </div>
            )}
        </div>
    );
}

export default App;